import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../interceptor/axiosInstance";
import { Form, Button, Container, Row, Col, Card } from "react-bootstrap";
import NotificationService from "../Utils/NotificationService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../Organisation/NewTicket.css";

const modules = {
  toolbar: false,
};

const editorStyles = {
  height: "150px",
  overflowY: "auto",
  padding: "5px",
  borderRadius: "0 0 5px 5px",
  width: "100%",
};

const NewTicket = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contactName: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
    status: "",
    ticketOwner: "Unassigned",
    attachments: [],
  });

  const [statuses, setStatuses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [activeField, setActiveField] = useState(""); // Track active input for dropdown
  const dropdownRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [statusRes, agentRes, contactRes] = await Promise.all([
          axiosInstance.get("/api/ticket/getStatuses"),
          axiosInstance.get("/api/ticket/getAgentList"),
          axiosInstance.get("/client/list"),
        ]);
        setStatuses(statusRes.data);
        setAgents(agentRes.data);
        setContacts(contactRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Handle Outside Click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilteredContacts([]);
        setActiveField("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "attachments") {
      setFormData({ ...formData, attachments: Array.from(files) });
    } else {
      setFormData({ ...formData, [name]: value });
      if (name === "contactName" || name === "email") {
        filterContacts(name, value);
        setActiveField(name); // Track the active input field
      }
    }
  };

  // Filter Contacts
  const filterContacts = (field, value) => {
    if (!value) {
      setFilteredContacts([]);
      return;
    }
    const searchField = field === "contactName" ? "name" : field;
    const results = contacts.filter((contact) =>
      contact[searchField]?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredContacts(results);
  };

  // Handle Contact Selection
  const handleContactSelect = (contact) => {
    setFormData({
      ...formData,
      contactName: contact.name,
      email: contact.email,
      phone: contact.phone_number || "",
    });
    setFilteredContacts([]);
    setActiveField("");
  };

  // Handle Quill Change
  const handleQuillChange = (content) => {
    setFormData((prevState) => ({ ...prevState, description: content }));
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.ticketOwner === "Unassigned") {
      formData.ticketOwner = null;
    }
    try {
      const formDataToSubmit = new FormData();
      for (let key in formData) {
        if (key === "attachments") {
          formData.attachments.forEach((file) => {
            formDataToSubmit.append("attachments", file);
          });
        } else {
          formDataToSubmit.append(key, formData[key]);
        }
      }

      const response = await axiosInstance.post("/tickets/create", formDataToSubmit, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        if (response.data && response.data.success) {
        NotificationService.success(response.data.message);
        navigate("/all-tickets");
        } else {
           NotificationService.error(response.data.message || "Error submitting ticket. Please try again.");
        }
      } else {
        NotificationService.info(response.data.message);
      }
    } catch (err) {
      NotificationService.error(err.message || "Error submitting ticket.");
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container className="mt-3">
      <Card className="shadow border-0">
        <Card.Body>
          <h3 className="text-center mb-4">Create New Ticket</h3>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6} className="position-relative">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Contact Name<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="Enter contact name"
                    required
                    className="rounded-pill"
                    autocomplete="off"
                    />
                  {activeField === "contactName" && filteredContacts.length > 0 && (
                    <ul ref={dropdownRef} className="autocomplete-list">
                      {filteredContacts.map((contact) => (
                        <li
                          key={contact.id}
                          onClick={() => handleContactSelect(contact)}
                          className="autocomplete-item"
                        >
                          {contact.name} ({contact.email})
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>
              </Col>
              <Col md={6} className="position-relative">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Email<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                    className="rounded-pill"
                     autocomplete="off"
                    />
                  {activeField === "email" && filteredContacts.length > 0 && (
                    <ul ref={dropdownRef} className="autocomplete-list">
                      {filteredContacts.map((contact) => (
                        <li
                          key={contact.id}
                          onClick={() => handleContactSelect(contact)}
                          className="autocomplete-item"
                        >
                          {contact.email} ({contact.name})
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="rounded-pill"
                     autocomplete="off"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Subject<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Enter subject"
                    required
                    className="rounded-pill"
                     autocomplete="off"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description<span className="text-danger">*</span></Form.Label>
              <ReactQuill
                modules={modules}
                theme="snow"
                value={formData.description}
                onChange={handleQuillChange}
                placeholder="Type a message..."
                style={editorStyles} // Apply custom styles to the content area
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Status<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    className="rounded-pill"
                  >
                    <option value="">Select Status</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Owner</Form.Label>
                  <Form.Select
                    name="ticketOwner"
                    value={formData.ticketOwner}
                    onChange={handleChange}
                    className="rounded-pill"
                  >
                    <option value="0">Unassigned</option>
                    {agents.map((agent) => (
                      <option key={agent.user_id} value={agent.user_id}>
                        🟢 {agent.initials} - {agent.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Attachments</Form.Label>
              <Form.Control
                type="file"
                name="attachments"
                multiple // Allow multiple file selection
                onChange={handleChange}
                className="rounded-pill"
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button
                type="submit"
                className="me-2 btn-color"
                style={{
                  borderRadius: "4px", // Square styling
                  width: "100px",
                  height: "40px",
                }}
              >
                Submit
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="btn-secondary-color"
                onClick={handleCancel}
                style={{
                  borderRadius: "4px", // Square styling
                  width: "100px",
                  height: "40px",
                  color: 'black'
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default NewTicket;
